import { ArchiveAnalysisForm } from '@modules/analysis/components/ArchiveAnalysisForm';
import { useChangeAnalysisScope } from '@modules/analysis/hooks/useChangeAnalysisScope';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { MenuItemOptions } from '@modules/common/components/KebabMenu/types';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { EMPTY_ARRAY } from '@modules/common/constants';
import {
  useExportFeedbackViaEmail,
  useFetchRecordCount,
} from '@modules/common/hooks';
import { isSavedSearchAuthor } from '@modules/feed/components/SearchAnalysisToolbar/utils';
import { useSearchAnalysesWhereClause } from '@modules/feed/hooks';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { ChatRounded, Inventory2Rounded } from '@mui/icons-material';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import { PermissionOperation, Scope } from 'generated/graphql';
import { useCallback, useMemo } from 'react';
import { TimeRange } from 'v2/core/entities';
import { SuperPermissionResource } from 'v2/core/entities/user';
import { useGetExportFeedbackRecordsModal } from 'v2/views/components/ExportFeedbackRecordsModalContent/hooks';
import { shallow } from 'zustand/shallow';

export const useGetSearchMoreActions = () => {
  const { id, isEditable, metadata } = useAnalysisStore(
    ({ editToken, id, metadata }) => ({
      id: id,
      isEditable: Boolean(editToken),
      metadata,
    }),
    shallow,
  );
  const isSystemGenerated = useAnalysisStore(
    ({ scope }) => scope === Scope.SystemGenerated,
  );

  const { openModal } = useMuiModalContext();

  const handleChangeSearchScope = useChangeAnalysisScope({
    analysisMetadata: metadata,
  });

  const search = useAnalysisStore((state) => state);
  const duration = useFeedStore(({ query }) => query.duration);
  const whereClause = useSearchAnalysesWhereClause();
  const queryContainer = {
    AggregatePeriod: 2,
    DateRange: duration as TimeRange,
    WhereClause: whereClause,
  };

  const [exportFeedbackViaEmail] = useExportFeedbackViaEmail({
    exportedFrom: 'Search Page',
  });

  const { data: feedbackCount } = useFetchRecordCount({
    queryContainer,
  });

  const exportFeedbackModal = useGetExportFeedbackRecordsModal({
    feedbackCount,
    exportFeedback: async ({ includeContent, includeOriginalContent }) => {
      await exportFeedbackViaEmail({
        queryContainer,
        includeContent,
        includeOriginalContent,
      });
    },
  });

  const handleOpenExportFeedbackModal = useCallback(() => {
    exportFeedbackModal();
  }, [exportFeedbackModal]);

  const openArchiveModal = useCallback(() => {
    if (!search) {
      return;
    }
    openModal({
      contentComponent: () => (
        <ArchiveAnalysisForm
          analysis={search}
          queryMetadata={search.metadata}
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 1,
        },
      },
    });
  }, [search, openModal]);

  const shouldAllowEditActions = isSavedSearchAuthor({
    analysisID: id,
    isEditable,
  });
  const canAccessDiscoverability = useIsAuthorized({
    resource: SuperPermissionResource.Discoverability,
    operation: PermissionOperation.Write,
  });

  const moreActions: MenuItemOptions = useMemo(
    () => [
      ...(shouldAllowEditActions && canAccessDiscoverability
        ? [
            {
              icon: <TravelExploreRoundedIcon fontSize="small" />,
              label: 'Change Discoverability',
              onClick: handleChangeSearchScope,
            },
          ]
        : []),
      {
        icon: <ChatRounded fontSize="small" />,
        label: 'Export Feedback',
        onClick: handleOpenExportFeedbackModal,
      },
      ...(shouldAllowEditActions
        ? [
            {
              icon: <Inventory2Rounded fontSize="small" />,
              label: 'Archive',
              onClick: openArchiveModal,
            },
          ]
        : []),
    ],
    [
      shouldAllowEditActions,
      canAccessDiscoverability,
      handleChangeSearchScope,
      handleOpenExportFeedbackModal,
      openArchiveModal,
    ],
  );

  return isSystemGenerated ? EMPTY_ARRAY : moreActions;
};
